import { createSelector } from 'reselect'
import { get, orderBy, sumBy, values, filter, map, isEmpty } from 'lodash'
import moment from 'moment'

import getMatchingRecords from '../utils/getMatchingRecords'
import {
  createOrderParams,
  getOrderIsUnsyncable,
  inValidOrderStates,
  orderReplayRequestId,
  sortedOrders,
} from '../utils/orderUtils'
import { isCreditType, STADIUM_ORDER_STATES } from '../utils/orderStates'

import { getIsPaxDevice } from './peripheral'
import { getTabsModeEnabled } from './menus'
import { isOfflineApproval } from '../utils/offlineReplayStatuses'
import { currentOrderUuid } from '../utils/orderNumbers'
import { getCurrentMenuId } from './appState'
import { OPEN_PRICE_REASON, PRICE_ADJUST_REASON } from '../VNItems/consts'

/**
 *
 * @param {state} state
 * @returns The first order UUID in the stored orders or undefined if there are no orders.
 */
export const getLatestOrderUuid = (state) => {
  try {
    return Object.keys(get(state, `order.byId`))[0]
  } catch (e) {
    return undefined
  }
}

export const getIsOrderCancelled = (state) => state?.order?.isCancelled ?? false

/**
 *
 * @param {state} state the redux state
 * @returns a mapping of request ID to order with collisions resolved by most recent order
 * TODO (mkramerl): Move to a paradigm where storage of orders is by request ID / invoice to support
 *     better uniqueness and collision detection.
 * @deprecated do not use, search by order ID instead
 */
export const getRequestToOrderMapping = (state) => {
  const orders = values(get(state, 'order.byId', {}))

  return sortedOrders(orders, false).reduce((accumulator, order) => {
    const requestId = orderReplayRequestId(order)

    if (requestId) {
      accumulator[requestId] = order
    }

    return accumulator
  }, {})
}

export const getUpdatedSince = (state) => moment(get(state, 'order.updatedSince', moment())).toISOString()

export const getPurgedAt = (state) => get(state, 'order.purgedAt', undefined)

export const getKioskPayment = (state) => get(state, 'order.kioskPayment', {})

export const getPurgableOrders = (state, msThreshold, options) => {
  const ordersById = get(state, 'order.byId', {})
  const conditionalAttribute = options?.force ? 'submittedAt' : 'syncedAt'

  // An order is purgable if
  //   * it was synced to the platform; AND
  //   * it was synced the processor; AND
  //   * it is older than the specified threshold
  const isPurgable = (order) =>
    order?.wasCreatedInStadium &&
    (createOrderParams(order)?.state !== STADIUM_ORDER_STATES.APPROVED_OFFLINE ||
      createOrderParams(order)?.state !== STADIUM_ORDER_STATES.TABBED ||
      createOrderParams(order)?.state !== STADIUM_ORDER_STATES.TABBED_OFFLINE) &&
    moment().diff(moment(order[conditionalAttribute])) >= msThreshold
  const syncedOrdersIds = map(filter(values(ordersById), isPurgable), 'uuid')

  return syncedOrdersIds
}

export const getMaintenancedAt = (state) => {
  return state?.order?.maintenancedAt
}

export const getMaintenanceEnabled = (state) => {
  return state?.order?.maintenanceEnabled === undefined ? true : state?.order?.maintenanceEnabled
}

export const getSafeToPerformMaintenance = (state) => {
  const offlineLocalIds = state?.order?.offlineLocalIds ?? []
  const offlineCardIds = state?.order?.offlineCardIds ?? []
  const failureRemoteIds = state?.order?.failureRemoteIds ?? []

  return [offlineLocalIds, offlineCardIds, failureRemoteIds].every(isEmpty)
}

/**
 * @deprecated use hook useOrdersList instead
 */
export const getOrders = (state, isRefundReceiptsEnabled) => {
  // TODO: [T36617] determinng factor is current menu until T36715 is
  // addressed on a per-order basis
  const tabsModeEnabled = getTabsModeEnabled(state)
  const currentMenuUuid = getCurrentMenuId(state)
  const byId = get(state, 'order.byId', {})

  let orders = []
  orders = values(byId)

  const excludedStates = [
    STADIUM_ORDER_STATES.SUBMISSION_FAILED,
    STADIUM_ORDER_STATES.SUBMITTED,
    STADIUM_ORDER_STATES.AUTHORIZATION_FAILED,
  ]

  // Add refunded or cancelled orders to the list of orders to display.
  if (!isRefundReceiptsEnabled) {
    excludedStates.push(STADIUM_ORDER_STATES.CANCELLED)
  }

  // Filter orders by state.
  orders = orders?.filter((order) => {
    const curState = createOrderParams(order)?.state || order.state

    if (tabsModeEnabled) {
      if (currentMenuUuid !== order?.standMenuUuid) {
        return false
      }

      if (curState === STADIUM_ORDER_STATES.AUTHORIZATION_FAILED && !order?.refundable) {
        return true
      }
    }

    return !excludedStates.includes(curState)
  })

  const search = get(state, 'order.ordersSearch')
  if (search) {
    orders = getFilteredOrders(orders, search)
  }

  const nonRefundable = []
  const refundable = []

  sortedOrders(orders).forEach((order) => {
    const collection = order?.refundable ? refundable : nonRefundable
    collection.push(order)
  })

  return [...nonRefundable, ...refundable]
}

export const getUnsyncedOrderCounts = (state) => {
  const unsyncedLocalOrderCount = makeGetOfflineLocalIds()(state)?.length ?? 0
  let activeOfflineCardCount = state?.order?.offlineCardIds?.length ?? 0

  if (getIsPaxDevice(state)) {
    activeOfflineCardCount = getActiveOfflineCardCount(state)
  }

  return [activeOfflineCardCount, unsyncedLocalOrderCount]
}

/**
 * Check if the Sync Orders Yello Banner should be displayed
 */
export const shouldDisplayOfflineTransactionHeader = createSelector(
  getUnsyncedOrderCounts,
  ([activeOfflineCardCount, unsyncedLocalOrderCount]) => activeOfflineCardCount > 0 || unsyncedLocalOrderCount > 0,
)

// returns true if there are non defective orders to sync
export const shouldInitiateIdleSync = (state) => {
  const unsyncedLocalOrderCount = makeGetOfflineLocalIds()(state)?.length ?? 0

  if (unsyncedLocalOrderCount > 0) return true

  const byId = get(state, 'order.byId', {})
  const orders = values(byId)

  const offlineApprovalOrders = orders.filter((order) => {
    //don't count orders that don't have cardReaderData
    if (!order.cardReaderData) return false

    //don't count unsyncable orders
    if (getOrderIsUnsyncable(order)) return false

    //only count legit offline approvals
    return isOfflineApproval(order)
  })

  return offlineApprovalOrders.length > 0
}

export const getAllOrders = (state) => {
  const byId = get(state, 'order.byId', {})
  return byId
}

export const getOrderSearchTerm = (state) => state.order.ordersSearch

const getTabOrders = (state) => {
  const byId = get(state, 'order.byId', {})
  let tabOrders = values(byId)

  const sortedOrders = orderBy(tabOrders, (order) => moment(order?.submittedAt), ['desc'])
  const closed = []
  const open = []

  sortedOrders.forEach((order) => {
    const collection = order?.orderState === 'open' ? open : closed
    collection.push(order)
  })

  return [...open, ...closed]
}

export const getFilteredOrders = (orders, search) => {
  return orders?.filter((o) => {
    if ((o?.tabName || '').toString().toLowerCase().includes(search.toLowerCase())) {
      return true
    }

    if (!search.match(/^[0-9a-z]+$/)) {
      return false
    }

    if ((o.orderNumber || '').toString().includes(search)) {
      return true
    }

    if (o?.payments?.length > 1) {
      return (
        o?.payments?.filter((payment) => {
          return (payment?.userAttributes?.last4 || '').toString().includes(search)
        })?.length > 0
      )
    }

    return (o?.userAttributes?.last4 || '').toString().includes(search)
  })
}

export const makeGetTabOrders = () => createSelector([getTabOrders], (tabOrders) => tabOrders)

const getTabOrder = (state, orderId) => get(state, `order.byId.${orderId}`, {})

export const makeGetTabOrder = () => createSelector(getTabOrder, (tabOrder) => tabOrder)

export const makeGetOrders = () => createSelector([getOrders], (orders) => orders)

export const getOrder = (state, id) => get(state, `order.byId.${id}`, {})

export const makeGetOrder = () => createSelector([getOrder], (order) => order)

export const getOrderCounts = (state) => {
  const failure = get(state, 'order.failureRemoteIds', [])
  const offline = get(state, 'order.offlineLocalIds', [])
  const offlineCardIds = get(state, 'order.offlineCardIds', [])
  const success = get(state, 'order.successRemoteIds', [])
  const byId = get(state, 'order.byId', {})
  const filterByDate = (orders) => {
    let syncedOrders = []
    orders.forEach((id) => {
      if (!isEmpty(byId[id])) {
        syncedOrders.push(byId[id])
      }
    })
    return syncedOrders.filter((order) => moment(order?.syncedAt).isSame(moment(), 'day'))
  }

  return {
    failure: filterByDate(failure).length,
    offline: offline.length + offlineCardIds.length,
    success: filterByDate(success).length,
  }
}

export const makeGetOrderCounts = () => createSelector([getOrderCounts], (counts) => counts)

export const getSelectedOrder = (state) => {
  const orderId = get(state, 'order.selectedOrderId')
  return get(state, `order.byId[${orderId}]`)
}

export const getSelectedOrderId = () => (state) => state?.order?.selectedOrderId

export const getOrderPayments = createSelector([getSelectedOrder], (order) => order?.payments ?? [])

export const getOrderAmountInCents = createSelector([getSelectedOrder], (order) => order?.amountInCents ?? 0)

export const getOrderIsTabbed = createSelector(
  [getSelectedOrder],
  (order) =>
    order?.state === STADIUM_ORDER_STATES.TABBED ||
    createOrderParams(order)?.state === STADIUM_ORDER_STATES.TABBED ||
    order?.state === STADIUM_ORDER_STATES.TABBED_OFFLINE ||
    createOrderParams(order)?.state === STADIUM_ORDER_STATES.TABBED_OFFLINE,
)

export const getIsAuthFailedOrder = createSelector([getSelectedOrder], (order) => {
  if (order?.state === STADIUM_ORDER_STATES.PARTIALLY_REFUNDED && order?.balanceDueInCents > 0) return true

  return order?.state === STADIUM_ORDER_STATES.AUTHORIZATION_FAILED
})

export const getUnsyncableIds = (state) => state?.order?.unsyncableIds ?? []

export const makeGetSelectedOrder = () => createSelector([getSelectedOrder], (order) => order)

const dateForOrder = (order) => {
  return moment(
    order?.syncedAt || order?.updatedAt || order?.modifiedAt || order?.submittedAt || order?.createdAt || order?.timestamp,
  )
}

/**
 * While there is a property in order reducer called 'order.offlineLocalIds',
 * 'offlineLocalIds' is not updated or used anymore
 * 'isSyncing' is only true when order is getting created
 * we must look at the others themselves to determine if they need to be sent to stadium
 * and if the last interaction with Stadium failed or not.
 */
export const getOfflineLocalIds = (state, limit) => {
  const orders = state?.order?.byId ?? {}

  // TODO: T41812 - this will be replaced with a Set instead of looking at order flags
  const matcher = (order) => (!order?.wasCreatedInStadium && !order?.isSyncing) || order?.needsSync
  const result = getMatchingRecords(Object.keys(orders), orders, matcher, limit)

  return result

  // Legacy Code
  // const orders = state?.order?.byId ?? {}
  // const orderIds = state?.order?.offlineLocalIds ?? []

  // if (!isFinite(limit)) {
  //   return orderIds
  // } else {
  //   const matcher = (order) => (order?.uuid || order?.id) !== null && (order?.uuid || order?.id) !== undefined
  //   const result = getMatchingRecords(orderIds, orders, matcher, limit)

  //   return result
  // }
}

// deprecated: use useOrderCounters hook instead
export const getSyncableOfflineLocalIds = (state, limit = undefined, byPassActiveOrder = false) => {
  const orders = state?.order?.byId ?? {}
  const currentMoment = moment()
  const orderInProgressUuid = state?.order?.orderInProgress?.uuid ?? null

  // TODO: T41812 - this will be replaced with a Set instead of looking at order flags
  const matcher = (order) => {
    const curOrderId = order?.uuid || order?.id
    if (byPassActiveOrder && curOrderId === orderInProgressUuid) {
      return false
    }

    const orderDate = dateForOrder(order)
    let withinLastDay = false
    if (orderDate) {
      const hoursDifference = Math.abs(currentMoment.diff(orderDate, 'hours'))
      withinLastDay = hoursDifference <= 24
    }

    // Attempt the platform sync if:
    //   * the order is not unsyncable (to prevent re-syncing orders that are already synced)
    //   * the order was not created in stadium and is not syncing
    //   * the order is within the last 24 hours
    //   * the order is not an auth failed order (to prevent re-syncing auth failed orders)
    //   * conditionally set to ignore tab orders
    return (
      !order?.unsyncable &&
      ((!order?.wasCreatedInStadium && !order?.isSyncing) || order?.needsSync) &&
      order?.state !== STADIUM_ORDER_STATES.AUTHORIZATION_FAILED &&
      withinLastDay
    )
  }

  // Get all orders that match the matcher function and sort them by date in descending order
  // so that the most recent orders are at the top and will be synced first.
  return getMatchingRecords(Object.keys(orders), orders, matcher, limit).sort((a, b) => {
    const dateA = dateForOrder(orders[a])
    const dateB = dateForOrder(orders[b])

    return dateA - dateB
  })
}

export const makeGetOfflineLocalIds = () => createSelector([getOfflineLocalIds], (ids) => ids)

export const getOfflineCardIds = (state) => {
  const orderIds = state?.order?.offlineCardIds ?? []

  return orderIds
}

// deprecated: use useOrderCounters hook instead
export const getOrderIdsToStateSync = (state, includeSubmitted = true) => {
  const orderIds = state?.order?.offlineCardIds ?? []

  // Append orders with an unknown order state, like "submitted", even if they are not in the
  // offlineCardIds
  const orders = state?.order?.byId ?? {}
  const currentMoment = moment()

  const matcher = (order) => {
    const orderDate = dateForOrder(order)
    let withinSevenDays = false
    if (orderDate) {
      const daysDifference = Math.abs(currentMoment.diff(orderDate, 'days'))
      withinSevenDays = daysDifference <= 7
    }

    // If the order is submitted OR in the list of offlineCardIds, we want to sync it as long as it is within 7 days.
    return (
      ((includeSubmitted && order?.state === STADIUM_ORDER_STATES.SUBMITTED) ||
        orderIds.includes(order?.id?.toString()) ||
        orderIds.includes(order?.uuid?.toString())) &&
      withinSevenDays
    )
  }

  // Get all orders that match the matcher function and sort them by date in descending order
  // so that the most recent orders are at the top and will be synced first.
  const ordersToSync = getMatchingRecords(Object.keys(orders), orders, matcher).sort((a, b) => {
    const dateA = dateForOrder(orders[a])
    const dateB = dateForOrder(orders[b])

    return dateA - dateB
  })

  return [...ordersToSync]
}

export const makeGetOfflineCardIds = () => createSelector([getOfflineCardIds], (ids) => ids)

// Check if there are any offline orders by Ids
export const hasLocalOfflineOrders = (state) => {
  if (state?.order?.offlineLocalIds?.length) {
    return true
  }

  if (state?.order?.offlineCardIds?.length) {
    return true
  }

  return false
}

// Used to check which orders were requested upload in card reader
export const getActiveOfflineCardCount = (state) => {
  return state?.order?.activeOfflineCardCount ?? 0
}

export const getAllRevenueInCents = (state) => {
  const orders = values(get(state, 'order.byId', {}))
  const validOrders = filter(orders, (order) => !inValidOrderStates.includes(createOrderParams(order)?.state))
  return sumBy(validOrders, 'amountInCents')
}

export const makeGetAllRevenueInCents = () => createSelector([getAllRevenueInCents], (cents) => cents)

export const getStoredRickCheckoutOrderUuid = (state) => get(state, 'order.storedOrderUuid', '')

export const getStoredRickCheckoutOrderNumber = (state) => get(state, 'order.storedOrderNumber', '')

export const getOrderInProgress = (state) => state?.order?.orderInProgress ?? {}

export const hasOrderOpenPriceItem = createSelector([getOrderInProgress], (orderInProgress) => {
  return orderInProgress?.itemModels?.some((item) => item.openPrice && item.reason === OPEN_PRICE_REASON)
})

export const tabbedOrderHasPriceOverrideItem = createSelector([getOrderInProgress], (orderInProgress) => {
  return orderInProgress?.itemModels?.some((item) => item.reason === PRICE_ADJUST_REASON)
})

export const getUserUuidFromOrderInProgress = (state) => state?.order?.orderInProgress?.userUuid

export const hasCardPaymentInProgress = (state) => {
  const payments = state?.order?.orderInProgress?.payments
  if (!payments?.length) {
    return false
  }

  if (payments.filter((payment) => isCreditType(payment?.paymentType))?.length) {
    return true
  }
  return false
}

export const getOrdersFilter = (state) => state?.order?.ordersFilter ?? {}

export const getMutationInProgress = (state) => state.order.mutationInProgress || false

export const getQRPayAuthError = (state) => state.order.QRPayAuthError || false

export const getOrderReportsLastFetchedAt = (state) => state.order.orderReportsLastFetchedAt ?? ''

export const getCurrentOrderIsInCancelledState = (state) => {
  const uuid = currentOrderUuid()
  if (!state.order.byId?.hasOwnProperty(uuid)) return false
  return state.order.byId[uuid]?.state === STADIUM_ORDER_STATES.CANCELLED
}

export const getIsLoadingOrders = (state) => state.order.isLoading || false
